import { useGetUsersWithFlags } from '@actions';
import { ReactComponent as BlockIcon } from '@assets/icons/V4/block-icon.svg';
import ExternalLinkIcon from '@assets/icons/external-link.svg';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Table, { SortOrder } from '@components/V4/Table/Table';
import { useQueryParamsState } from '@hooks';
import DetailsModal, {
  DetailsModalPage,
} from '@modules/Admin/components/DetailsModal/DetailsModal';
import { UserWithFlags } from '@shared/clientTypes/DTOs/FlagsDTO';
import { FlagStatus, UserRole } from '@shared/enums';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import EmptyStateCard from 'apps/agora/src/views/UserView/components/EmptyStateCard';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { UserFlagsModal } from './UsersFlagsModal';

type SortField = 'fullName' | 'activeFlags' | 'closedFlags' | 'totalFlags' | 'createdAt';

interface UsersHealthProps {
  userType: UserRole.Mentor | UserRole.Student;
}

const useQuery = () => new URLSearchParams(useLocation().search);

type SearchParams = {
  page: string;
  limit: string;
  sortBy: SortField;
  sortOrder: SortOrder;
  search: string;
  isActive: string;
  flagType: string;
  studentId: string;
  mentorId: string;
  courseId: string;
  userStatus: string;
};

export function UsersHealth(props: UsersHealthProps) {
  const { userType } = props;
  const query = useQuery();

  const page = query.get('page') || '1';
  const limit = query.get('limit') || '10';
  const sortBy = (query.get('sortBy') as SortField) || 'activeFlags';
  const sortOrder = (query.get('sortOrder') as SortOrder) || 'desc';
  const search = query.get('search') || '';
  const isActive = query.get('isActive') || FlagStatus.ACTIVE?.toString();
  const flagType = query.get('flagType') || '';
  const studentId = query.get('studentId') || '';
  const mentorId = query.get('mentorId') || '';
  const courseId = query.get('courseId') || '';
  const userStatus = query.get('userStatus') || 'indeterminate';

  const [searchParams, setSearchParams] = useQueryParamsState<SearchParams>({
    page,
    limit,
    sortBy,
    sortOrder,
    search,
    isActive,
    flagType,
    studentId,
    mentorId,
    courseId,
    userStatus,
  });

  const [selectedUser, setSelectedUser] = useState<UserWithFlags | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>();
  const [modalPage, setModalPage] = useState<DetailsModalPage>('student');

  const { data: usersWithFlags, isLoading } = useGetUsersWithFlags({
    role: userType,
    page: searchParams.page,
    limit: searchParams.limit,
    sortBy: searchParams.sortBy,
    sortOrder: searchParams.sortOrder || undefined,
    search: searchParams.search,
    isActive: searchParams.isActive === FlagStatus.ACTIVE?.toString() ? 'true' : 'false',
    flagType: searchParams.flagType,
    studentId: searchParams.studentId,
    mentorId: searchParams.mentorId,
    courseId: searchParams.courseId,
    userStatus: searchParams.userStatus,
  });

  const userClickHandler = (user: UserWithFlags) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const modalCloseHandler = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const userDetailsClickHandler = (user: UserWithFlags) => {
    setSelectedUserId(user._id);
    setModalPage(userType === UserRole.Mentor ? 'mentor' : 'student');
    setShowDetailsModal(true);
  };

  const detailsModalCloseHandler = () => {
    setShowDetailsModal(false);
    setSelectedUserId(undefined);
  };

  const sortChangeHandler = (sortBy: SortField | null, sortOrder: SortOrder) => {
    setSearchParams({
      sortBy: sortBy || 'activeFlags',
      sortOrder: sortOrder || 'desc',
      page: '1',
    });
  };

  const pageChangeHandler = (page: number) => {
    setSearchParams({ page: page.toString() });
  };

  const pageSizeChangeHandler = (limit: number) => {
    setSearchParams({ limit: limit.toString(), page: '1' });
  };

  const sortedUsers = [...(usersWithFlags?.users || [])].sort((a, b) => {
    if (!sortBy || !sortOrder) return 0;

    const multiplier = sortOrder === 'asc' ? 1 : -1;

    switch (sortBy) {
      case 'fullName':
        return a.fullName.localeCompare(b.fullName) * multiplier;
      case 'activeFlags':
        return (a.activeFlags - b.activeFlags) * multiplier;
      case 'closedFlags':
        return (a.closedFlags - b.closedFlags) * multiplier;
      case 'totalFlags':
        return (a.activeFlags + a.closedFlags - (b.activeFlags + b.closedFlags)) * multiplier;
      case 'createdAt':
        return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * multiplier;
      default:
        return 0;
    }
  });

  return (
    <div className="p-6 py-8">
      <Table
        className="table-fixed"
        pagination={{
          pageSize: Number(limit),
          total: usersWithFlags?.total,
          currentPage: Number(page),
          onCurrentPageChange: pageChangeHandler,
          onPageSizeChange: pageSizeChangeHandler,
        }}
      >
        <Table.Header>
          <Table.Row isHeader>
            <Table.HeaderCell
              onSort={(order) => sortChangeHandler('fullName', order)}
              sortOrder={sortBy === 'fullName' ? sortOrder : undefined}
              className="w-[40%] truncate"
            >
              Name
            </Table.HeaderCell>
            <Table.HeaderCell
              onSort={(order) => sortChangeHandler('activeFlags', order)}
              sortOrder={sortBy === 'activeFlags' ? sortOrder : undefined}
              className="w-[12%] truncate"
            >
              Active Flags
            </Table.HeaderCell>
            <Table.HeaderCell
              onSort={(order) => sortChangeHandler('closedFlags', order)}
              sortOrder={sortBy === 'closedFlags' ? sortOrder : undefined}
              className="w-[12%] truncate"
            >
              Closed Flags
            </Table.HeaderCell>
            <Table.HeaderCell
              onSort={(order) => sortChangeHandler('totalFlags', order)}
              sortOrder={sortBy === 'totalFlags' ? sortOrder : undefined}
              className="w-[12%] truncate"
            >
              Total Flags
            </Table.HeaderCell>
            <Table.HeaderCell
              onSort={(order) => sortChangeHandler('createdAt', order)}
              sortOrder={sortBy === 'createdAt' ? sortOrder : undefined}
              className="w-[12%] truncate"
            >
              Last Flag Created At
            </Table.HeaderCell>
            <Table.HeaderCell className="w-[12%] truncate">Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {!isLoading ? (
            sortedUsers.map((user) => (
              <Table.Row key={user._id}>
                <Table.Cell className="w-[40%] truncate">
                  <span
                    onClick={() => userDetailsClickHandler(user)}
                    className={mergeClassNames(
                      'text-customPrimary hover:text-customPrimary/80 cursor-pointer'
                    )}
                  >
                    {user.fullName}
                  </span>
                </Table.Cell>
                <Table.Cell className="w-[12%] truncate">{user.activeFlags}</Table.Cell>
                <Table.Cell className="w-[12%] truncate">{user.closedFlags}</Table.Cell>
                <Table.Cell className="w-[12%] truncate">
                  {user.activeFlags + user.closedFlags}
                </Table.Cell>
                <Table.Cell className="w-[12%] truncate">
                  {(user.createdAt && new Date(user.createdAt).toLocaleDateString()) || '-'}
                </Table.Cell>
                <Table.Cell className="w-[12%] truncate">
                  <div onClick={() => userClickHandler(user)} className="flex gap-1 cursor-pointer">
                    <img className="w-3.5" src={ExternalLinkIcon} alt="flags" title="View Flags" />
                    <span className="min-w-max text-white">View Flags</span>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))
          ) : (
            <tr>
              <td colSpan={6} className="h-64">
                <div className="w-full h-full flex justify-center items-center">
                  <LoadingSpinner className="w-5 h-5" />
                </div>
              </td>
            </tr>
          )}

          {!isLoading && !usersWithFlags?.users?.length && (
            <tr>
              <td colSpan={6} className="h-64">
                <EmptyStateCard
                  icon={<BlockIcon className="text-customGrey" />}
                  text={'No results.'}
                />
              </td>
            </tr>
          )}
        </Table.Body>
      </Table>

      {showModal && selectedUser && (
        <UserFlagsModal
          isOpen={showModal}
          onClose={modalCloseHandler}
          user={selectedUser}
          userType={userType}
        />
      )}

      {showDetailsModal && selectedUserId && (
        <DetailsModal
          page={modalPage}
          isOpen={true}
          onClose={detailsModalCloseHandler}
          {...(userType === UserRole.Mentor
            ? { mentorId: selectedUserId }
            : { studentId: selectedUserId })}
        />
      )}
    </div>
  );
}
